import { RootActions } from 'types/state';
import { RootActionTypes } from '@redux/modules/actionTypes';
import * as ActionTypes from '../actionTypes';
import { ClearErrorsAction, ReportErrorsAction } from '../actions';

export interface ErrorCode {
  section: string
  subsection: string
  code: string
  uid: string
  valid: boolean
}

const defaultState = {};

// returns the list of errors with those from this subsection removed
const clearErrors = (sectionErrors: ErrorCode[], action: ClearErrorsAction): ErrorCode[] => sectionErrors.filter(
  (x) => x.section === action.property && x.subsection !== action.subsection
);

const errorMatches = (err1: ErrorCode, err2: ErrorCode) =>
  // uid needs to be compared to capture item errors from sections that have multiple items in an accordion, otherwise they get overwritten as a duplicate
  err1.section === err2.section && err1.code === err2.code && err1.uid === err2.uid; // eslint-disable-line implicit-arrow-linebreak
const getErrorIndex = (sectionErrors: ErrorCode[], err: ErrorCode) => sectionErrors.findIndex((x) => errorMatches(x, err));

// modifies the sectionErrors
const updateError = (sectionErrors: ErrorCode[], err: ErrorCode) => {
  const index = getErrorIndex(sectionErrors, err);
  if (index === -1) {
    sectionErrors.push(err);
  } else {
    sectionErrors[index] = err; // eslint-disable-line no-param-reassign
  }
};

// modifies the sectionErrors
const updateErrors = (sectionErrors: ErrorCode[], action: ReportErrorsAction): ErrorCode[] => {
  const newErrors = [...sectionErrors];
  for (const err of action.values) {
    updateError(newErrors, err);
  }

  return newErrors;
};

const errorReducer = (state = defaultState, action: RootActions) => {
  let sectionErrors;

  switch (action.type) {
    case ActionTypes.ERROR_CLEAR:
      // @ts-ignore
      sectionErrors = (state[action.property] || []) as ErrorCode[];

      // This resolves a memory leak. When we're on Review, this will clear
      // out all existing errors for the entire section and no longer
      // continously build up.
      if (action.subsection === 'review') {
        return {
          ...state,
          [action.property]: [],
        };
      }
      return {
        ...state,
        [action.property]: clearErrors(sectionErrors, action),
      };

    case ActionTypes.ERROR_UPDATE:
      sectionErrors = (state[action.property as keyof typeof state] || []) as ErrorCode[];

      return {
        ...state,
        [action.property]: updateErrors(sectionErrors, action),
      };

    case RootActionTypes.authentication.LOGGED_OFF: {
      return {
        ...defaultState,
      };
    }
    default:
      return state;
  }
};

export default errorReducer;
